import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveMenu, fetchSettings } from '../../Redux/Action';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CardHeader,
    CardContent,
    Collapse,
} from "@mui/material";
import {
    Settings as SettingsIcon,
    Security as SecurityIcon,
    Notifications as NotificationIcon,
    Power as PowerIcon,
    People as RosterIcon,
    ExpandLess,
    ExpandMore,
    Login as LoginIcon,
    VpnKey as TwoFactorIcon,
    CloudSync as SSOIcon,
    Password as PasswordIcon,
    Security as IpWhitelistIcon,
    History as ActivityIcon,
    Storage as StorageIcon,
    Event as HolidayIcon,
    EventNote as EventNoteIcon,
    Apartment as VenueIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { SidebarCard } from "../../../../../Components/Cards/SidebarCard";
import { usePermissions } from "../../../../../Hooks/usePermissions";

const StyledList = styled(List)(({ theme }) => ({
    '& .MuiListItem-root': {
        marginBottom: theme.spacing(0.5),
        borderRadius: theme.spacing(1),
    },
    '& .MuiListItemButton-root': {
        borderBottom: `1px dashed ${theme.palette.divider}`,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            '& .MuiListItemIcon-root': {
                color: theme.palette.text.primary,
            }
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            color: theme.text.dark,
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
            },
            '& .MuiListItemIcon-root': {
                color: theme.text.dark,
            },
            '& .MuiListItemText-primary': {
                color: theme.text.dark,
            }
        },
        '& .MuiListItemText-primary': {
            fontSize: '0.8rem!important',
            color: theme.palette.secondary.dark,
        },
        '&:last-child': {
            borderBottom: 'none',
        }
    },
    '& .MuiListItemIcon-root': {
        color: theme.palette.text.secondary,
    },
    // Nested list styling (for Collapse sub-items)
    '& .MuiList-root': {
        '& .MuiListItemButton-root': {
            borderBottom: `1px dashed ${theme.palette.divider}`,
            '&:last-child': {
                borderBottom: 'none',
            }
        }
    }
}));

// Category icon mapping
const categoryIcons = {
    'default': <SettingsIcon />,
    'general-settings': <SettingsIcon />,
    'security-settings': <SecurityIcon />,
    'login-settings': <LoginIcon />,
    'two-factor-auth': <TwoFactorIcon />,
    'sso-settings': <SSOIcon />,
    'password-policy': <PasswordIcon />,
    'ip-whitelist': <IpWhitelistIcon />,
    'activity-log': <ActivityIcon />,
    'token': <SecurityIcon />,
    'notification-settings': <NotificationIcon />,
    'microsoft-settings': <PowerIcon />,
    'roster-settings': <RosterIcon />,
    'storage': <StorageIcon />,
};

const menuItems = [
    {
        id: 'general',
        label: 'General',
        icon: <SettingsIcon />,
        description: 'Basic application settings',
        visible: true,
        subItems: [] // Will be populated from database
    },
    // {
    //     id: 'security',
    //     label: 'Security',
    //     icon: <SecurityIcon />,
    //     description: 'Security and authentication settings',
    //     visible: true,
    //     subItems: [] // Will be populated from database
    // },
    {
        id: 'notification',
        label: 'Notification',
        icon: <NotificationIcon />,
        description: 'Email and notification preferences',
        visible: true,
        subItems: [] // Will be populated from database
    },
    {
        id: 'integration',
        label: 'Integration',
        icon: <PowerIcon />,
        description: 'Third-party integrations',
        visible: false,
        subItems: [] // Will be populated from database
    },
    {
        id: 'roster',
        label: 'Roster',
        icon: <RosterIcon />,
        description: 'Roster management settings',
        visible: true,
        subItems: [] // Will be populated from database
    },
    {
        id: 'data',
        label: 'Data',
        icon: <StorageIcon />,
        description: 'Data management and configuration',
        visible: true,
        subItems: [
            // { id: 'data-status', label: 'Status Management', icon: <StatusIcon /> },
            // { id: 'data-entities', label: 'Entities', icon: <EntityIcon /> },
            { id: 'data-holidays', label: 'Holidays', icon: <HolidayIcon /> },
            { id: 'data-events', label: 'Events', icon: <EventNoteIcon /> },
            { id: 'data-venues', label: 'Venues', icon: <VenueIcon /> },
        ]
    }
];

export const Sidebar = ({ selectedTab, onTabChange }) => {
    const dispatch = useDispatch();
    const { hasPermission } = usePermissions();
    const activeMenu = useSelector(state => state.settings['active-menu']);
    const [openGroups, setOpenGroups] = useState({});
    const [dynamicMenuItems, setDynamicMenuItems] = useState(menuItems);

    // Function to populate subItems from database
    const populateSubItems = useCallback(async () => {
        try {
            const response = await fetchSettings({ dispatch, filters: { paginate: 'false' } });
            const settings = response.data || [];
            
            // Group settings by category
            const categoryGroups = settings.reduce((groups, setting) => {
                const category = setting.category;
                if (!groups[category]) {
                    groups[category] = [];
                }
                groups[category].push(setting);
                return groups;
            }, {});

            // Update menu items with subItems from database
            const updatedMenuItems = menuItems.map(menuItem => {
                if (menuItem.id === 'data') {
                    // Keep data menu as is (hardcoded)
                    return menuItem;
                }
                
                // Find subItems for this main category
                const subItems = Object.keys(categoryGroups)
                    .filter(category => {
                        // Map database categories to main menu items
                        if (menuItem.id === 'general') {
                            //return ['general-settings', 'storage'].includes(category);
                            return ['general-settings'].includes(category);
                        } else if (menuItem.id === 'security') {
                            return ['login-settings', 'two-factor-auth', 'sso-settings', 'password-policy', 'ip-whitelist', 'activity-log', 'token'].includes(category);
                            //return false;
                        } else if (menuItem.id === 'notification') {
                            return ['notification-settings'].includes(category);
                        } else if (menuItem.id === 'integration') {
                            return ['microsoft-settings'].includes(category);
                        } else if (menuItem.id === 'roster') {
                            return ['roster-settings'].includes(category);
                        }
                        return false;
                    })
                    .map(category => ({
                        id: category,
                        label: category.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
                        icon: categoryIcons[category] || categoryIcons['default'],
                        count: categoryGroups[category].length
                    }));

                return {
                    ...menuItem,
                    subItems
                };
            });

            setDynamicMenuItems(updatedMenuItems);
        } catch (error) {
        }
    }, [dispatch]);

    // Populate subItems from database on component mount
    useEffect(() => {
        populateSubItems();
    }, [populateSubItems]);

    // Set active menu from Redux on component mount and expand parent groups
    useEffect(() => {
        if (activeMenu && activeMenu !== selectedTab) {
            onTabChange(activeMenu);
            
            // Find and expand parent group for sub-menu
            const parentGroup = dynamicMenuItems.find(item => 
                item.subItems && item.subItems.some(subItem => subItem.id === activeMenu)
            );
            if (parentGroup) {
                setOpenGroups(prev => ({
                    ...prev,
                    [parentGroup.id]: true
                }));
            }
        }
    }, [activeMenu, selectedTab, onTabChange, dynamicMenuItems]);

    const handleGroupToggle = (groupId) => {
        setOpenGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const handleItemClick = (itemId) => {
        dispatch(setActiveMenu(itemId));
        onTabChange(itemId);
    };

    return (
        <SidebarCard sx={{ height: 'fit-content' }}>
            <CardHeader
                title={"Settings"}
                subheader={"Manage your application preferences"}
            />
            <CardContent sx={{ p: 0 }}>
                <StyledList>
                    {dynamicMenuItems.filter(item => item.visible !== false).map((item) => {
                        const filteredSubItems = (item.id === 'data' && item.subItems)
                            ? item.subItems.filter((sub) => {
                                if (sub.id === 'data-holidays') {
                                    return hasPermission('core.settings.holiday.menu');
                                }
                                if (sub.id === 'data-venues') {
                                    return hasPermission('core.settings.venue.menu');
                                }
                                if (sub.id === 'data-events') {
                                    return hasPermission('core.settings.event.menu');
                                }
                                return true;
                            })
                            : (item.subItems || []);
                        const hasSubItems = filteredSubItems.length > 0;
                        const hasMultipleSubItems = filteredSubItems.length > 1;
                        const hasSingleSubItem = filteredSubItems.length === 1;
                        const isGroupOpen = openGroups[item.id];
                        const isGroupSelected = selectedTab.startsWith(item.id);
                        const singleSubItem = hasSingleSubItem ? filteredSubItems[0] : null;

                        // If only one subItem, render it as direct menu item
                        if (hasSingleSubItem) {
                            return (
                                <ListItem key={item.id} disablePadding>
                                    <ListItemButton
                                        selected={selectedTab === singleSubItem.id}
                                        onClick={() => handleItemClick(singleSubItem.id)}
                                        sx={{ borderRadius: 1, mx: 0.5 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            {singleSubItem.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={singleSubItem.label}
                                            secondary={singleSubItem.count ? `${singleSubItem.count} settings` : item.description}
                                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }

                        // If multiple subItems or no subItems, render as before
                        return (
                            <React.Fragment key={item.id}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={isGroupSelected && !hasSubItems}
                                        onClick={() => hasMultipleSubItems ? handleGroupToggle(item.id) : handleItemClick(item.id)}
                                        sx={{ borderRadius: 1, mx: 0.5 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            secondary={item.description}
                                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                        />
                                        {hasMultipleSubItems && (isGroupOpen ? <ExpandLess /> : <ExpandMore />)}
                                    </ListItemButton>
                                </ListItem>

                                {hasMultipleSubItems && (
                                    <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding sx={{ pl: 2 }}>
                                            {filteredSubItems.map((subItem) => (
                                                <ListItem key={subItem.id} disablePadding>
                                                    <ListItemButton
                                                        selected={selectedTab === subItem.id}
                                                        onClick={() => handleItemClick(subItem.id)}
                                                        sx={{ borderRadius: 1, mx: 0.5 }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            {subItem.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={subItem.label}
                                                            secondary={subItem.count ? `${subItem.count} settings` : ''}
                                                            primaryTypographyProps={{ fontSize: '0.8rem' }}
                                                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                )}
                            </React.Fragment>
                        );
                    })}
                </StyledList>
            </CardContent>
        </SidebarCard>
    );
};